import { useCallback, useEffect, useRef, useState } from "react";

/* ---------------------------------------------
 * Web Speech API (speechSynthesis) Hook
 * ------------------------------------------- */

type FinishCallback = () => void;

export const useWebSpeechSynthesis = () => {
  const [utterances, setUtterances] = useState<string[]>([]);
  const [onFinishCallbacks] = useState<FinishCallback[]>([]);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  const speak = useCallback(async (text: string): Promise<void> => {
    const utterThis = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    utterThis.voice =
      voices.find((v) => v.lang === "de-DE") ??
      voices.find((v) => v.lang?.startsWith?.("de")) ??
      null;
    utterThis.lang = "de-DE";
    utterThis.pitch = 1;
    utterThis.rate = 1;

    window.speechSynthesis.speak(utterThis);

    await new Promise<void>((res) => {
      utterThis.onend = () => res();
    });
  }, []);

  // Speak the last utterance and remove it from the queue once it finishes.
  // Keep doing this until there are no more utterances left.
  useEffect(() => {
    if (utterances.length === 0 || isPaused) return;

    const last = utterances[utterances.length - 1];
    let cancelled = false;

    speak(last).then(() => {
      if (cancelled || isPaused) return;

      if (utterances.length === 1) {
        onFinishCallbacks.forEach((cb) => cb());
      }
      setUtterances((current) => current.slice(0, -1));
    });

    return () => {
      cancelled = true;
    };
  }, [utterances, isPaused, speak, onFinishCallbacks]);

  const readText = (text: string) => {
    const segments = text
      .split(/[,\.;-]/gim)
      .map((s) => s.trim())
      .filter(Boolean);
    setUtterances([...segments.reverse()]);
  };

  const stopReading = () => {
    setUtterances([]);
    window.speechSynthesis.cancel();
  };

  const skipSentence = () => {
    window.speechSynthesis.cancel();
  };

  const pauseReading = () => {
    setIsPaused(true);
    window.speechSynthesis.pause();
  };

  const continueReading = () => {
    setIsPaused(false);
    window.speechSynthesis.resume?.();
  };

  return {
    readText,
    stopReading,
    skipSentence,
    pauseReading,
    continueReading,
  };
};

/* ---------------------------------------------
 * Google Cloud TTS utility with memoization
 * ------------------------------------------- */

type IdType = string | number;
type StepType = string | number;

const fetchSynthesizedAudio = (() => {
  const audios: Record<string, string> = {};
  const audioRefreshingTable: Record<string, boolean> = {};

  return async (id: IdType, step: StepType): Promise<string | null> => {
    const key = `${id}-${step}`;

    // Return cached audio if available
    if (audios[key]) return audios[key];

    // Refresh the audio for this ID only once
    if (!audioRefreshingTable[id as string]) {
      try {
        await fetch(
          `/api/refreshSoundFile?id=${encodeURIComponent(String(id))}`,
        );
      } catch {
        // Ignore refresh errors
      }
      audioRefreshingTable[id as string] = true;
    }

    // Try fetching pre-generated audio from your backend
    try {
      const resp = await fetch(
        `/api/getSoundFile?id=${encodeURIComponent(String(id))}&step=${encodeURIComponent(
          String(step),
        )}`,
      );

      if (resp.ok) {
        const txt = await resp.text();
        if (txt) {
          audios[key] = txt;
          return txt;
        }
      }
    } catch (e) {
      console.error(e);
    }

    // If no Google API key, return a silent placeholder audio
    const gKey = process.env.NEXT_PUBLIC_KEY;
    if (!gKey) {
      const silent =
        "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
      audios[key] = silent;
      return silent;
    }

    // Request a new synthesis from Google Cloud TTS
    try {
      const res = await fetch(
        `https://texttospeech.googleapis.com/v1/text:synthesize?key=${gKey}`,
        {
          headers: { "Content-Type": "application/json; charset=utf-8" },
          method: "POST",
          body: JSON.stringify({
            input: { text: String(step) }, // You might change this to the actual sentence
            voice: {
              languageCode: "de-DE",
              name: "de-DE-Wavenet-B",
              ssmlGender: "MALE",
            },
            audioConfig: { audioEncoding: "MP3" },
          }),
        },
      );

      if (!res.ok) throw new Error("Google Cloud TTS request failed");

      const data: { audioContent?: string } = await res.json();
      if (data.audioContent) {
        const dataUrl = "data:audio/mpeg;base64," + data.audioContent;
        audios[key] = dataUrl;
        return dataUrl;
      }
    } catch (e) {
      console.warn(e);
    }

    return null;
  };
})();

/* ---------------------------------------------
 * Google Cloud TTS Hook (HTMLAudioElement)
 * ------------------------------------------- */

export const useGoogleCloudSpeechSynthesis = () => {
  const sound = useRef<HTMLAudioElement | null>(null);

  const readText = async (id: IdType, step: StepType) => {
    const audio = await fetchSynthesizedAudio(id, step);
    if (!audio || !sound.current) return;

    sound.current.src = audio;
    sound.current.play().catch(console.warn);

    const endListener = () => {
      sound.current?.removeEventListener("ended", endListener);
    };

    sound.current.addEventListener("ended", endListener);
  };

  const stopReading = () => {
    if (sound.current) sound.current.src = "";
  };

  const pauseReading = () => {
    sound.current?.pause();
  };

  const continueReading = () => {
    sound.current?.play().catch(console.warn);
  };

  const muteReading = () => {
    if (sound.current) sound.current.volume = 0;
  };

  const unmuteReading = () => {
    if (sound.current) sound.current.volume = 1;
  };

  // Initialize audio and make it playable on mobile after first user interaction
  useEffect(() => {
    sound.current = new Audio();
    sound.current.autoplay = true;

    // Set an initial silent MP3 so that it can be preloaded
    sound.current.src =
      "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";

    // Try playing silently on load, or wait for first user click
    sound.current.play().catch(() => {
      document.onclick = () => {
        document.onclick = null;
        if (!sound.current) return;

        sound.current
          .play()
          .then(() => {
            sound.current?.pause();
            if (sound.current) sound.current.src = "";
          })
          .catch(console.warn);
      };
    });
  }, []);

  return {
    readText,
    stopReading,
    pauseReading,
    continueReading,
    muteReading,
    unmuteReading,
  };
};
